'use strict'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Link, Checkbox } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import CheckBoxRountedIcon from '@mui/icons-material/CheckBoxRounded'
import { titleName, viewId } from '../../../_constants/Code'
import { useToast } from '../../../_components/Toast'
import { errorLog } from '../../../common/logger/Logger'
import { useConfirmationModal } from '../../../_components/Modal/useConfirmationModal'
import { fetchJudgmentRuleList, deleteJudgmentRule } from './extra/services'
import { buildDeleteConfirmDescription } from './extra/deleteConfirm'
import { JudgmentRuleRowSchema } from './extra/types'

/**
 * 判定ルール条件式から対象期間ラベルを推定（PeriodKind 未設定時のフォールバック）
 *
 * @param {{ Condition?: string; condition?: string }[] | undefined} judgmentRules
 * @returns {'月次' | '四半期' | '年次' | ''}
 */
const inferPeriodKindLabelFromConditions = (judgmentRules) => {
  if (!Array.isArray(judgmentRules)) return ''
  const text = judgmentRules.map((r) => r?.Condition ?? r?.condition ?? '').join(' ')
  if (/\[(今四半期|前四半期)\./.test(text)) return '四半期'
  if (/\[(今月|前月)\./.test(text)) return '月次'
  if (/\[(今年度|前年度|今年度末月次|前年度末月次)\./.test(text)) return '年次'
  return ''
}

/**
 * API PeriodKind を対象期間の表示ラベル（月次/四半期/年次）に変換
 *
 * @param {string | undefined | null} periodKind
 * @param {{ Condition?: string; condition?: string }[] | undefined} [judgmentRules]
 * @returns {'月次' | '四半期' | '年次' | ''}
 */
const formatPeriodKindLabel = (periodKind, judgmentRules) => {
  const normalized = String(periodKind ?? '')
    .trim()
    .toLowerCase()
  if (normalized === 'monthly') return '月次'
  if (normalized === 'quarter' || normalized === 'quarterly') return '四半期'
  if (normalized === 'year' || normalized === 'yearly') return '年次'
  return inferPeriodKindLabelFromConditions(judgmentRules)
}

/**
 * 判定ルール一覧コンテンツコンポーネント
 *
 * @param {Object} props
 * @param {string} props.corporationNumber
 * @param {string} props.userId
 * @param {Object} props.authority
 * @param {string} props.pageTitle
 */
const JudgmentRuleListContent = (props) => {
  const [isLoaded, setIsLoaded] = React.useState(false)
  const [judgmentRuleList, setJudgmentRuleList] = React.useState([])
  const [errorMessage, setErrorMessage] = React.useState('')
  const [filterStatus, setFilterStatus] = React.useState({
    fix: true, // 変更可
    pending: true // 変更不可
  })
  const navigate = useNavigate()
  const showToast = useToast()
  const openConfirmationModal = useConfirmationModal()

  React.useEffect(() => {
    document.title = props.pageTitle
    init()
  }, [])

  /** 初期化処理 */
  const init = async () => {
    try {
      setErrorMessage('')
      const rows = await fetchJudgmentRuleList(props.corporationNumber)
      const displayList = rows.map((row) => convertToDisplayFormat(row))
      // 登録日時降順でソート
      displayList.sort((a, b) => {
        const dateA = a.createdAt || ''
        const dateB = b.createdAt || ''
        return dateB.localeCompare(dateA)
      })
      setJudgmentRuleList(displayList)
      setIsLoaded(true)
    } catch (err) {
      errorLog('E000001', props.userId, viewId.judgmentRuleList, 'init', err)
      setErrorMessage('データ取得エラーが発生しました')
      setIsLoaded(true)
    }
  }

  /**
   * DynamoDBデータを画面表示用データに変換
   *
   * @param {import('./extra/types').JudgmentRuleRow} row
   * @returns {Object} 画面表示用データ
   */
  const convertToDisplayFormat = (row) => {
    const periodKindLabel = formatPeriodKindLabel(row.PeriodKind, row.JudgmentRules)
    const hasUpdateInfo = Boolean(row.UpdateUserName) && Boolean(row.UpdateDateTime)
    return {
      id: row.RuleId,
      name: row.RuleName || '無題のルール',
      startMonth: row.ValidStartMonth || '',
      endMonth: row.ValidEndMonth || '',
      periodKindLabel,
      dataKindName: row.DataKindName || '',
      chrofyColumnName: row.ChrofyColumnName || '',
      createdBy: hasUpdateInfo ? row.UpdateUserName : row.CreateUserName || 'システム',
      createdAt: hasUpdateInfo ? row.UpdateDateTime : row.CreateDateTime || '',
      status: Array.isArray(row.Status) ? row.Status : [],
      periodNameList: Array.isArray(row.PeriodNameList) ? row.PeriodNameList : []
    }
  }

  /**
   * Yyyy/mm形式をyyyy年mm月形式に変換
   *
   * @param {string} monthStr Yyyy/mm形式の文字列
   * @returns {string} Yyyy年mm月形式の文字列
   */
  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return ''
    const parts = monthStr.split('/')
    if (parts.length === 2) {
      return `${parts[0]}年${parts[1]}月`
    }
    return monthStr
  }

  /**
   * 有効期間の表示文字列を生成（yyyy年mm月形式）
   *
   * @param {string} startMonth Yyyy/mm形式の開始月
   * @param {string} endMonth Yyyy/mm形式の終了月
   * @returns {string}
   */
  const formatPeriod = (startMonth, endMonth) => {
    if (!startMonth && !endMonth) return '-'
    const formattedStart = formatMonthDisplay(startMonth)
    const formattedEnd = formatMonthDisplay(endMonth)
    if (startMonth && endMonth) return `${formattedStart} ～ ${formattedEnd}`
    if (startMonth) return `${formattedStart} ～`
    return `～ ${formattedEnd}`
  }

  /**
   * 判定結果の設定先の表示文字列を生成（対象期間 / データ分類 / データ項目）
   *
   * @param {string} periodKindLabel 対象期間の表示ラベル（月次/四半期/年次）
   * @param {string} targetCategory データ分類
   * @param {string} targetField データ項目
   * @returns {string}
   */
  const formatTargetDestination = (periodKindLabel, targetCategory, targetField) => {
    const parts = [periodKindLabel || '-', targetCategory || '-', targetField || '-']
    return parts.join(' / ')
  }

  /**
   * 削除処理
   *
   * @param {string} ruleId
   */
  const handleDelete = async (ruleId) => {
    try {
      // 削除対象のルールを取得
      const targetRule = judgmentRuleList.find((rule) => rule.id === ruleId)
      const hasUsedStatus = Array.isArray(targetRule?.status) && targetRule.status.includes('used')

      // 説明文を生成（usedが含まれる場合は警告メッセージと期間名リストを追加）
      const periodNames = Array.isArray(targetRule?.periodNameList) ? targetRule.periodNameList : []
      const periodNamesText = periodNames.length > 0 ? periodNames.join('、') : ''
      const description = buildDeleteConfirmDescription({ hasUsedStatus, periodNamesText })

      await openConfirmationModal({
        title: '判定ルールの削除',
        description,
        confirmName: '削除',
        onClick: async () => {
          setErrorMessage('')
          try {
            await deleteJudgmentRule(props.corporationNumber, ruleId, props.userId)
            showToast('判定ルールを削除しました')
            // 一覧を再表示
            await init()
          } catch (deleteErr) {
            // 削除処理のエラーをハンドリング
            errorLog('E000001', props.userId, viewId.judgmentRuleList, 'handleDelete', deleteErr)
            const apiMessage = deleteErr.response?.data?.message ?? deleteErr.response?.body?.message
            let errorMessage =
              typeof apiMessage === 'string' && apiMessage.trim() !== '' ? apiMessage : '削除処理でエラーが発生しました'
            if (deleteErr.response?.status === 404) {
              errorMessage = '判定ルールが見つかりません'
            } else if (deleteErr.response?.status === 500) {
              errorMessage = 'サーバーエラーが発生しました'
            } else if (deleteErr.message?.includes('Network') || deleteErr.message?.includes('network')) {
              errorMessage = '通信エラーが発生しました'
            }
            setErrorMessage(errorMessage)
          }
        }
      })
    } catch (err) {
      // モーダルキャンセル時のエラーのみここで処理
      if (err && err.message === 'Cancelled') {
        // キャンセルの場合は何もしない
      }
    }
  }

  /** 新規登録ボタンクリック処理 */
  const handleNew = () => {
    navigate('/master/dataMapping/judgmentRuleSetting?new=true')
  }

  /**
   * フィルター状態変更処理
   *
   * @param {string} statusKey ステータスキー（fix, pending）
   */
  const handleFilterChange = (statusKey) => {
    setFilterStatus((prev) => ({
      ...prev,
      [statusKey]: !prev[statusKey]
    }))
  }

  /** フィルタリングされたルール一覧を取得 */
  const filteredRuleList = React.useMemo(() => {
    return judgmentRuleList.filter((rule) => {
      if (!Array.isArray(rule.status) || rule.status.length === 0) {
        // Statusが空の場合は表示しない
        return false
      }

      // 選択されたフィルターのいずれかのステータスを含むルールを表示
      const hasFix = filterStatus.fix && rule.status.includes('fix')
      const hasPending = filterStatus.pending && rule.status.includes('pending')

      return hasFix || hasPending
    })
  }, [judgmentRuleList, filterStatus])

  if (!isLoaded) {
    return null
  }

  return (
    <React.Fragment>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                  </span>
                </Link>
              </div>
            </div>
            {/* コンテンツヘッダ */}
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">判定ルール一覧</h1>
            </header>

            {/* コンテンツボディ */}
            <div className="page-body">
              {/* ===== 判定ルール一覧 ===== */}
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-16 px-16 border-blue1">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  判定ルール一覧
                </h2>

                {/* 説明文と新規追加ボタン */}
                <div className="flex items-center justify-between my-6">
                  <p className="text-body text-h7 grey--text">
                    判定ルールの一覧を表示します。ルール名をクリックして編集できます。
                  </p>
                  <button
                    className="btn white--text link rounded-lg py-1.5 pl-7 pr-10 inline-flex items-center"
                    onClick={handleNew}
                  >
                    <span className="icon-plus-line icon-32px mr-1.5 my-px -translate-y-px"></span>
                    <span className="text-h7">新規登録</span>
                  </button>
                </div>

                {/* エラーメッセージ表示 */}
                {errorMessage && (
                  <div className="error-messages mb-4">
                    <span className="red--text">{errorMessage}</span>
                  </div>
                )}

                {/* フィルター */}
                <div className="box-xy-bordered rounded-t-2xl rounded-b-2xl mb-6">
                  <div className="flex mt-3 ml-4">
                    <div className="text-h7 grey--text mt-1">フィルター：</div>
                    <div className="flex mt-1">
                      <label className="ui-checkbox mb-3 ml-5 mr-5">
                        <Checkbox
                          size="small"
                          checkedIcon={<CheckBoxRountedIcon />}
                          checked={filterStatus.pending}
                          onChange={() => handleFilterChange('pending')}
                        />
                        <span className="ui-checkbox__label text-h6 font-Regular">変更可</span>
                      </label>
                      <label className="ui-checkbox mb-3 ml-5 mr-5">
                        <Checkbox
                          size="small"
                          checkedIcon={<CheckBoxRountedIcon />}
                          checked={filterStatus.fix}
                          onChange={() => handleFilterChange('fix')}
                        />
                        <span className="ui-checkbox__label text-h6 font-Regular">変更不可</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* 一覧テーブル */}
                {filteredRuleList.length > 0 ? (
                  <div className="base-table">
                    <table className="base-table table-fixed w-full">
                      <thead className="base-table-header">
                        <tr>
                          <th className="w-1/5">ルール名</th>
                          <th className="w-1/5">有効期間</th>
                          <th className="w-[22%]">判定結果の設定先</th>
                          <th className="w-[12%]">登録者</th>
                          <th className="w-[18%]">登録日時</th>
                          <th className="w-[8%]">操作</th>
                        </tr>
                      </thead>
                      <tbody className="text-caption-1">
                        {filteredRuleList.map((rule) => (
                          <tr key={rule.id}>
                            <td>
                              <Link
                                underline="none"
                                component={RouterLink}
                                to={`/master/dataMapping/judgmentRuleSetting?id=${rule.id}`}
                                className="link-name"
                              >
                                {rule.name}
                              </Link>
                            </td>
                            <td className="text-caption-1 grey--text">
                              {formatPeriod(rule.startMonth, rule.endMonth)}
                            </td>
                            <td className="text-caption-1 grey--text">
                              {formatTargetDestination(rule.periodKindLabel, rule.dataKindName, rule.chrofyColumnName)}
                            </td>
                            <td>{rule.createdBy}</td>
                            <td>{rule.createdAt || '-'}</td>
                            <td>
                              <button
                                className="btn bg-red-100 text-red-800 py-1.5 px-3 text-xs"
                                onClick={() => handleDelete(rule.id)}
                                disabled={Array.isArray(rule.status) && rule.status.includes('fix')}
                              >
                                削除
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center my-10">
                    <span className="text-h5 grey--text">登録済みの判定ルールがありません</span>
                  </div>
                )}

                {/* 戻るボタン */}
                <footer className="content-footer my-8">
                  <div className="footer_button_area mt-8 justify-center">
                    <Link
                      color="inherit"
                      variant="button"
                      underline="none"
                      component={RouterLink}
                      to="/master/masterList"
                    >
                      <button type="button" className="footer-btn button_back">
                        戻る
                      </button>
                    </Link>
                  </div>
                </footer>
              </div>
              {/* ===== /判定ルール一覧 ===== */}
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  )
}

export default JudgmentRuleListContent
